import { Box, Grid2 as Grid, Stack, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useGet from "../../../services/hooks/useGet";
import usePut from "../../../services/hooks/usePut";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import { AlertType } from "../../../shared/constants/enums";
import { Button, Heading } from "../../../shared/ReusableComponents";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { DateFormatter } from "../../../shared/utils/DateFormatter";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import { toast } from "../../../store/toastSlice";
import { FluidOrderDialog } from "./FluidOrderDialog";

export const StudentFluidOrder = ({ caseId }: { caseId: any }) => {
  const commonCellStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    height: "100%",
  };
  const columns: GridColDef[] = [
    {
      field: "createdOn",
      headerName: "Date, Time",
      valueGetter: (value, row) =>
        `${DateFormatter.formatDate(value)}, ${DateFormatter.formatTime(
          row.createdOn
        )}`,
    },

    {
      field: "indication",
      headerName: "Indication",
      valueGetter: (value, row) => row.indication,
    },
    {
      field: "fluidType",
      headerName: "Fluid Type",
      valueGetter: (value, row) => row.fluidType,
    },
    {
      field: "volume",
      headerName: "Volume (mL)",
      valueGetter: (value, row) => row.volume,
    },
    {
      field: "additiveAndAmount",
      headerName: "Additive and Amount",
      valueGetter: (value, row) =>
        row.additiveAmount ? row.additiveAmount : "-",
    },
    {
      field: "rate",
      headerName: "Rate (mL/hr)",
      valueGetter: (value, row) => (row.rate ? row.rate : "-"),
    },
    {
      field: "route",
      headerName: "Route",
      valueGetter: (value, row) => row.route,
    },
    {
      field: "prescribedBy",
      headerName: "Prescribed By",
      valueGetter: (value, row) =>
        CommonFunctions.getDisplayValue(row.prescribedBy),
    },

    {
      field: "startDate",
      headerName: "Start Date",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const { startDate, volume, counterSignedBy } = params.row;
        const counterSignedName = CommonFunctions.formatName(counterSignedBy, {
          suffixKey: "rn",
        });

        if (startDate) {
          return (
            <Tooltip
              title={`${DateFormatter.formatDate(
                params.row.startDate
              )} ${DateFormatter.formatTime(
                params.row.startDate
              )} ${volume}(mL) Administered by ${fullName} ${counterSignedName}`}
            >
              <span>
                {`${DateFormatter.formatDate(
                  params.row.startDate
                )} ${DateFormatter.formatTime(
                  params.row.startDate
                )} ${volume}(mL) Administered by ${fullName} ${counterSignedName}`}
              </span>
            </Tooltip>
          );
        }

        return (
          <Box sx={{ ...commonCellStyle }}>
            <Button
              onClick={() => handleAdministerClick(params.row, "start")}
              text="Administer"
              fullWidth={false}
              className="tinyBtn"
            />
          </Box>
        );
      },
    },
    {
      field: "finishDate",
      headerName: "Finish Date",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const { endDate, startDate, volume, finishCounterSignedBy } =
          params.row;
        const counterSignedName = CommonFunctions.formatName(
          finishCounterSignedBy,
          {
            suffixKey: "rn",
          }
        );

        if (!startDate) {
          return (
            <Box sx={commonCellStyle}>
              <span>-</span>
            </Box>
          ); // Only enable after start
        }

        if (endDate) {
          return (
            <Tooltip
              title={`${DateFormatter.formatDate(
                endDate
              )} ${DateFormatter.formatTime(
                endDate
              )} ${volume}(mL) Administered by ${fullName} ${counterSignedName}`}
            >
              <span>
                {`${DateFormatter.formatDate(
                  endDate
                )} ${DateFormatter.formatTime(
                  endDate
                )} ${volume}(mL) Administered by ${fullName} ${counterSignedName}`}
              </span>
            </Tooltip>
          );
        }

        return (
          <Box sx={commonCellStyle}>
            <Button
              onClick={() => handleAdministerClick(params.row, "finish")}
              text="Administer"
              fullWidth={false}
              className="tinyBtn"
            />
          </Box>
        );
      },
    },
  ];
  const currentCaseId = caseId;
  const [adminType, setAdminType] = useState<"start" | "finish">("start");
  const auth = useSelector((state: any) => state.login);
  const [adultFluidData, setAdultFluidData] = useState<any>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [administerStartDate, setAdministerStartDate] = useState(moment());
  const [administerStartTime, setAdministerStartTime] = useState(moment());
  const [administerEndDate, setAdministerEndDate] = useState(moment());
  const [administerEndTime, setAdministerEndTime] = useState(moment());
  const [counterSignedBy, setCounterSignedBy] = useState("");
  const [errors, setErrors] = useState<any>({
    administerStartDate: false,
    administerStartTime: false,
    administerEndDate: false,
    administerEndTime: false,
    counterSignedBy: false,
  });
  const {
    getService,
    data: getFluidResponse,
    loading: getFluidLoading,
  } = useGet();
  const { putService, data: putFluidRes } = usePut();

  const dispatch = useDispatch();

  const fullName = CommonFunctions.formatFirstAndLastName(
    auth?.userInfo?.userFirstName,
    auth?.userInfo?.userLastName,
    { suffixKey: "sn" }
  );
  const selectedRowRef = useRef<any>(null);
  const adminTypeRef = useRef<"start" | "finish">("start");

  const handleAdministerClick = (row: any, type: "start" | "finish") => {
    setSelectedRow(row);
    selectedRowRef.current = row;
    setAdminType(type);
    adminTypeRef.current = type;
    setOpenDialog(true);
    setErrors({
      administerStartDate: false,
      administerStartTime: false,
      administerEndDate: false,
      administerEndTime: false,
      counterSignedBy: false,
    });
  };

  useEffect(() => {
    if (!getFluidResponse) {
      getService(`${apiEndpoints.NURSING_FLUID_ORDER}/${currentCaseId}`);
    }
    if (getFluidResponse) {
      setAdultFluidData(getFluidResponse);
    }
  }, [getFluidResponse]);

  useEffect(() => {
    getFluidLoading && dispatch(showLoader());
    !getFluidLoading && dispatch(hideLoader());
  }, [getFluidLoading]);

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    resetFields();
  };
  const resetFields = () => {
    setAdministerStartDate(moment());
    setAdministerStartTime(moment());
    setAdministerEndDate(moment());
    setAdministerEndTime(moment());
    setCounterSignedBy("");
  };

  const handleSubmit = () => {
    const isStartDateInvalid =
      adminType === "start" &&
      (!administerStartDate ||
        !moment(administerStartDate, "DD/MM/YYYY", true).isValid());

    const isStartTimeInvalid =
      adminType === "start" &&
      (!administerStartTime ||
        !moment(administerStartTime, "HH:mm", true).isValid());

    const isEndDateInvalid =
      adminType === "finish" &&
      (!administerEndDate ||
        !moment(administerEndDate, "DD/MM/YYYY", true).isValid());

    const isEndTimeInvalid =
      adminType === "finish" &&
      (!administerEndTime ||
        !moment(administerEndTime, "HH:mm", true).isValid());

    const newErrors = {
      administerStartDate: isStartDateInvalid,
      administerStartTime: isStartTimeInvalid,
      administerEndDate: isEndDateInvalid,
      administerEndTime: isEndTimeInvalid,
      counterSignedBy: counterSignedBy.trim() === "",
    };
    setErrors(newErrors);
    if (Object.values(newErrors).includes(true)) return;
    setErrors({
      administerStartDate: false,
      administerStartTime: false,
      administerEndDate: false,
      administerEndTime: false,
      counterSignedBy: false,
    });
    const updatedFluidOrderRecords = adultFluidData?.fluidOrderRecords.map(
      (item: any) => {
        if (item.id !== selectedRow.id) return item;

        return {
          ...item,
          ...(adminType === "start"
            ? {
                startAdministered: true,
                administerStartDate,
                administerStartTime,
              }
            : {
                finishAdministered: true,
                administerEndDate,
                administerEndTime,
              }),
        };
      }
    );
    const updatedData = {
      ...adultFluidData,
      fluidOrderRecords: updatedFluidOrderRecords,
    };
    setAdultFluidData(updatedData);

    setOpenDialog(false);
    setSelectedRow(null);
    resetFields();

    const combinedStartDate = moment(
      moment(administerStartDate).format("YYYY-MM-DD") +
        " " +
        moment(administerStartTime).format("HH:mm:ss")
    ).toISOString();

    const combinedEndDate = moment(
      moment(administerEndDate).format("YYYY-MM-DD") +
        " " +
        moment(administerEndTime).format("HH:mm:ss")
    ).toISOString();

    const payload =
      adminType === "start"
        ? {
            id: selectedRow?.id,
            startDate: combinedStartDate,
            counterSignedBy,
            endDate: null,
            finishCounterSignedBy: null,
          }
        : {
            id: selectedRow?.id,
            startDate: null,
            counterSignedBy: null,
            endDate: combinedEndDate,
            finishCounterSignedBy: counterSignedBy,
          };

    putService(apiEndpoints.NURSING_ADMINSTER_FLUIDORDER, payload, "");
  };

  useEffect(() => {
    if (putFluidRes) {
      const row = selectedRowRef.current;
      const type = adminTypeRef.current;

      const message =
        type === "start"
          ? `${row?.fluidType} has been Successfully Started.`
          : `${row?.fluidType} has been Successfully Completed.`;
      dispatch(
        toast({
          message,
          type: AlertType.SUCCESS,
        })
      );
      setOpenDialog(false);
      setSelectedRow(null);
      resetFields();
      getService(`${apiEndpoints.NURSING_FLUID_ORDER}/${currentCaseId}`);
    }
  }, [putFluidRes]);

  const handleCounterSignedByChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCounterSignedBy(e.target.value);
    if (e.target.value.trim())
      setErrors((prev: any) => ({ ...prev, counterSignedBy: false }));
  };
  const comorbidityList: Record<string, string> = {
    isHeartFailure: "Heart Failure",
    isKidneyFailure: "Kidney Failure",
    isLiverFailure: "Liver Failure",
    isDiabetes: "Diabetes",
    isNil: "Nil",
    other: adultFluidData?.otherDescription || "",
  };

  const comorbidityText = Object.entries(comorbidityList)
    .filter(([key, _]) => adultFluidData?.[key])
    .map(([key, label]) => label)
    .filter(Boolean) // Remove empty strings
    .join(", ");

  const displayComorbidityText = comorbidityText ? comorbidityText : "-";
  const instructionText = adultFluidData?.instructions
    ? adultFluidData?.instructions
    : "-";
  const fluidRestrictText = adultFluidData?.fluidRestriction
    ? adultFluidData.fluidRestriction
    : "-";

  const fluidOrderRows =
    adultFluidData?.fluidOrderRecords?.map((item: any) => ({
      ...item,
      id: item.id,
    })) || [];

  return (
    <React.Fragment>
      {adultFluidData?.fluidOrderRecords?.length > 0 ? (
        <>
          <Grid
            sx={{
              backgroundColor: "#F5F5F5",
              marginLeft: "10px",
              padding: "10px",
            }}
          >
            <Heading text="Instructions" isBold style={{ fontSize: "1rem" }} />
            <Heading text={instructionText} style={{ fontSize: "1rem" }} />
            <div style={{ margin: "10px 0" }} />
            <Heading
              text="Relevant Comorbidities"
              isBold
              style={{ fontSize: "1rem" }}
            />
            <Heading
              text={displayComorbidityText}
              style={{ fontSize: "1rem" }}
            />
            <div style={{ margin: "10px 0" }} />
            <Grid spacing={1} sx={{ display: "flex", alignItems: "center" }}>
              <Heading
                text="Fluid Restriction"
                isBold
                style={{ fontSize: "1rem" }}
              />
              &nbsp;
              <Heading
                text="(*Consider checking bloods / electrolytes where applicable)"
                style={{ fontSize: "0.75rem" }}
              />
            </Grid>
            <Heading
              text={
                !adultFluidData?.fluidRestriction?.trim() &&
                adultFluidData?.isNilRestriction
                  ? "Nil"
                  : fluidRestrictText
              }
              style={{ fontSize: "1rem" }}
            />
          </Grid>

          <div
            className="iconHeading"
            style={{ marginTop: "10px", marginLeft: "10px" }}
          >
            <Heading type="h5" isBold={true} text="Prescribed Fluid List" />
          </div>
          <div style={{ marginLeft: "-10px", marginRight: "-20px" }}>
            <GridTable
              rows={fluidOrderRows}
              columns={columns}
              pageSizeOptions={[5, 10, 15]}
              initialPageSize={10}
              showSearchBar={false}
            />
          </div>
        </>
      ) : (
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="center"
          fontSize="1rem"
          color={"var(--color-border-model)"}
        >
          No data available.
        </Stack>
      )}
      <FluidOrderDialog
        openDialog={openDialog}
        handleClose={handleClose}
        adminType={adminType}
        adultFluidData={adultFluidData}
        administerStartDate={administerStartDate}
        setAdministerStartDate={setAdministerStartDate}
        administerStartTime={administerStartTime}
        setAdministerStartTime={setAdministerStartTime}
        administerEndDate={administerEndDate}
        setAdministerEndDate={setAdministerEndDate}
        administerEndTime={administerEndTime}
        setAdministerEndTime={setAdministerEndTime}
        counterSignedBy={counterSignedBy}
        handleCounterSignedByChange={handleCounterSignedByChange}
        handleSubmit={handleSubmit}
        errors={errors}
        setErrors={setErrors}
        selectedRow={selectedRow}
      />
    </React.Fragment>
  );
};
